import React, { useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ShieldCheck, KeyRound, Eye, EyeOff, CheckCircle2 } from 'lucide-react';

// Map user role to dashboard path after auto-login
const ROLE_REDIRECT = {
  admin: '/admin-dashboard',
  manager: '/staff-dashboard',
  counsellor: '/staff-dashboard',
  staff: '/staff-dashboard',
  recruiter: '/recruiter-dashboard',
  student: '/student-portal',
};

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const tokenMissing = !token;

  const strength = useMemo(() => {
    if (!password) return { score: 0, label: '' };
    let s = 0;
    if (password.length >= 8) s += 1;
    if (password.length >= 12) s += 1;
    if (/[A-Z]/.test(password) && /[a-z]/.test(password)) s += 1;
    if (/\d/.test(password)) s += 1;
    if (/[^A-Za-z0-9]/.test(password)) s += 1;
    const labels = ['Weak', 'Weak', 'Fair', 'Good', 'Strong', 'Excellent'];
    return { score: s, label: labels[s] };
  }, [password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ token, new_password: password }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.detail || 'Could not reset password.');
      }
      setSuccess(true);
      const role = data?.user?.role;
      const redirectTo = ROLE_REDIRECT[role] || '/login';
      setTimeout(() => navigate(redirectTo), 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-tge-paper">
      <main className="flex-grow flex items-center justify-center px-4 pt-[100px] pb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 0.9, 0.32, 1] }}
          className="w-full max-w-md bg-white rounded-3xl shadow-[0_20px_60px_rgba(27,42,107,0.12)] overflow-hidden"
          data-testid="reset-password-card"
        >
          <div className="p-8 sm:p-10">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-xs text-tge-muted hover:text-tge-navy transition"
              data-testid="reset-password-back"
            >
              <ArrowLeft size={14} /> Back to all portals
            </Link>

            <div className="w-14 h-14 rounded-2xl bg-tge-navy flex items-center justify-center mt-6 shadow-lg">
              <KeyRound className="text-white" size={26} strokeWidth={1.8} />
            </div>

            <h1 className="font-serif-display text-3xl text-tge-navy mt-6">Set a new password</h1>
            <p className="text-tge-muted text-sm mt-2 leading-relaxed">
              Choose a strong password. Once saved you'll be signed in automatically.
            </p>

            {tokenMissing ? (
              <div
                className="mt-7 rounded-2xl border border-rose-200 bg-rose-50 p-5"
                data-testid="reset-password-token-missing"
              >
                <p className="text-sm text-rose-800 font-semibold">Reset link is missing or invalid.</p>
                <p className="text-sm text-rose-800/80 mt-2">
                  Please request a fresh reset link from the forgot-password page.
                </p>
                <Link
                  to="/forgot-password"
                  className="mt-4 inline-flex items-center justify-center w-full h-11 rounded-xl bg-tge-navy hover:bg-tge-navy-deep text-white font-semibold text-sm transition"
                  data-testid="reset-password-request-new"
                >
                  Request a new link
                </Link>
              </div>
            ) : success ? (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-7 rounded-2xl border border-emerald-200 bg-emerald-50 p-5"
                data-testid="reset-password-success"
              >
                <div className="flex items-center gap-2 text-emerald-700 font-semibold">
                  <CheckCircle2 size={18} /> Password updated
                </div>
                <p className="text-sm text-emerald-800/80 mt-2 leading-relaxed">
                  You're being signed in and redirected to your dashboard…
                </p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="mt-7 space-y-4">
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-xl text-sm"
                    data-testid="reset-password-error"
                  >
                    {error}
                  </motion.div>
                )}

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label
                      className="block text-[12px] font-semibold uppercase tracking-wider text-tge-muted"
                      htmlFor="new-password"
                    >
                      New password
                    </label>
                    <button
                      type="button"
                      onClick={() => setShow((s) => !s)}
                      className="text-[11px] text-tge-muted hover:text-tge-navy transition inline-flex items-center gap-1"
                      data-testid="reset-password-toggle-visibility"
                    >
                      {show ? <EyeOff size={12} /> : <Eye size={12} />} {show ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  <input
                    id="new-password"
                    type={show ? 'text' : 'password'}
                    required
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    data-testid="reset-password-new"
                    className="w-full h-12 px-4 rounded-xl bg-tge-cream-soft border border-transparent focus:border-tge-gold focus:bg-white focus:outline-none focus:ring-4 focus:ring-tge-gold/15 transition placeholder:text-tge-muted/50 text-[15px]"
                  />
                  {password && (
                    <div className="mt-2 flex items-center gap-2" data-testid="reset-password-strength">
                      <div className="flex-1 h-1.5 rounded-full bg-tge-cream-soft overflow-hidden">
                        <div
                          className={`h-full transition-all ${
                            strength.score >= 4
                              ? 'bg-emerald-500'
                              : strength.score >= 3
                              ? 'bg-amber-500'
                              : 'bg-rose-400'
                          }`}
                          style={{ width: `${(strength.score / 5) * 100}%` }}
                        />
                      </div>
                      <span className="text-[11px] text-tge-muted">{strength.label}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label
                    className="block text-[12px] font-semibold uppercase tracking-wider text-tge-muted mb-1.5"
                    htmlFor="confirm-password"
                  >
                    Confirm password
                  </label>
                  <input
                    id="confirm-password"
                    type={show ? 'text' : 'password'}
                    required
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Re-enter the password"
                    data-testid="reset-password-confirm"
                    className="w-full h-12 px-4 rounded-xl bg-tge-cream-soft border border-transparent focus:border-tge-gold focus:bg-white focus:outline-none focus:ring-4 focus:ring-tge-gold/15 transition placeholder:text-tge-muted/50 text-[15px]"
                  />
                  {confirm && confirm !== password && (
                    <p className="text-[11px] text-rose-600 mt-1.5">Passwords do not match.</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || !password || password !== confirm}
                  data-testid="reset-password-submit"
                  className="w-full h-12 rounded-xl bg-tge-navy hover:bg-tge-navy-deep text-white font-semibold text-[15px] transition disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-tge-navy/20 inline-flex items-center justify-center gap-2"
                >
                  {loading ? 'Updating…' : 'Update password & sign in'}
                  {!loading && <ShieldCheck size={16} className="opacity-80" />}
                </button>

                <div className="flex items-center gap-2 text-[11px] text-tge-muted/70 pt-1">
                  <ShieldCheck size={12} className="text-emerald-500" />
                  <span>Encrypted connection · cookies secured</span>
                </div>
              </form>
            )}
          </div>
        </motion.div>
      </main>
    </div>
  );
}
